import APIService from 'api/APIService'

/**
 * Thrown only when a session is definitely over and the user must log in again
 * (e.g. the refresh token was rejected). Transient failures (network, 5xx, 429,
 * timeouts) must NOT be turned into this error.
 */
export class SessionExpiredError extends Error {
  constructor(message = 'Session expired') {
    super(message)
    this.name = 'SessionExpiredError'
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getErrorStatus = (error: any): number | undefined =>
  error?.response?.status ?? error?.status

/** The server rejected the refresh token itself - re-login is the only way out. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isRefreshTokenRejected = (error: any) =>
  error instanceof SessionExpiredError ||
  [400, 401, 403].includes(getErrorStatus(error) ?? 0)

const fetchUser = async (accessToken: string) => ({
  user: await APIService.motus.getCurrentUser({ accessToken }),
  accessToken
})

/** Exchanges the refresh token for a new access token; throws SessionExpiredError if it is rejected. */
const refreshAccessToken = async (refreshToken: string) => {
  try {
    return await APIService.motus.getAccessToken({ refreshToken })
  } catch (error) {
    // Network/5xx/429 while refreshing is transient: rethrow as-is so the session is kept
    throw isRefreshTokenRejected(error) ? new SessionExpiredError() : error
  }
}

/**
 * Fetches the Motus user, refreshing a stale access token (~15 min lifetime) first when possible.
 *
 * Resolves with the user and the access token that worked. Throws `SessionExpiredError` only when
 * re-login is definitely required; any other error is transient and must not end the session.
 */
export const fetchMotusUserWithRefresh = async ({
  accessToken,
  refreshToken
}: {
  accessToken: string
  refreshToken: string | null
}) => {
  try {
    return await fetchUser(accessToken)
  } catch (error) {
    // Only a 401 means the access token is stale; anything else is not a session problem
    if (getErrorStatus(error) !== 401) throw error
    if (!refreshToken) throw new SessionExpiredError()
  }

  const freshToken = await refreshAccessToken(refreshToken)

  try {
    return await fetchUser(freshToken)
  } catch (error) {
    throw getErrorStatus(error) === 401 ? new SessionExpiredError() : error
  }
}
